import React, { lazy, Suspense } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation
} from "react-router-dom";

import { AuthProvider, useAuth } from "./context/AuthContext";

import { initAnalytics } from "./analytics";
import { startKeepAlive } from "./keepalive";

import PWAInstallPrompt from "./components/PWAInstallPrompt";
import InstallPrompt from "./components/InstallPrompt";

/*
 * Route-level code splitting.
 *
 * Home stays eager because it is the main feed.
 * Other pages are loaded only when the user navigates to them.
 */
import Home from "./pages/Home";

const Login = lazy(() => import("./pages/Login"));
const Register = lazy(() => import("./pages/Register"));
const Upload = lazy(() => import("./pages/Upload"));
const Profile = lazy(() => import("./pages/Profile"));
const Wallet = lazy(() => import("./pages/Wallet"));
const Discover = lazy(() => import("./pages/Discover"));
const Inbox = lazy(() => import("./pages/Inbox"));

const UserProfile = lazy(() => import("./components/UserProfile"));

const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));

const PasswordReset = lazy(() => import("./pages/PasswordReset"));
const VerifyEmail = lazy(() => import("./pages/VerifyEmail"));
const Contract = lazy(() => import("./pages/Contract"));

const About = lazy(() => import("./pages/legal/About"));
const Privacy = lazy(() => import("./pages/legal/Privacy"));
const Terms = lazy(() => import("./pages/legal/Terms"));
const Cookies = lazy(() => import("./pages/legal/Cookies"));
const CommunityGuidelines = lazy(() => import("./pages/legal/CommunityGuidelines"));
const AdvertiserPolicy = lazy(() => import("./pages/legal/AdvertiserPolicy"));
const CreatorPolicy = lazy(() => import("./pages/legal/CreatorPolicy"));
const DMCA = lazy(() => import("./pages/legal/DMCA"));
const Contact = lazy(() => import("./pages/legal/Contact"));


/*
 * Small loading screen used only while a lazy route
 * is being downloaded.
 */
function RouteLoading() {
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "#000",
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
      }}
    >
      <div
        style={{
          width: 32,
          height: 32,
          borderRadius: "50%",
          border: "3px solid #333",
          borderTopColor: "#fff",
          animation: "spin 0.6s linear infinite"
        }}
      />

      <style>
        {`
          @keyframes spin {
            to {
              transform: rotate(360deg);
            }
          }
        `}
      </style>
    </div>
  );
}


/*
 * Authentication-protected route.
 */
function PrivateRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return <RouteLoading />;
  }

  return user ? children : <Navigate to="/login" replace />;
}


/*
 * Admin authentication route.
 */
function AdminRoute({ children }) {
  const token = localStorage.getItem("admin_token");

  if (!token) {
    return <Navigate to="/admin-login" replace />;
  }

  return children;
}


/*
 * Main application layout.
 */
function Layout() {
  const location = useLocation();
  const { user } = useAuth();

  React.useEffect(() => {
    initAnalytics(user);
    startKeepAlive();
  }, [user]);

  React.useEffect(() => {
    if (window.posthog) {
      window.posthog.capture("$pageview");
    }
  }, [location.pathname]);

  /*
   * Navbar is intentionally disabled because the active
   * navigation is inside the VideoFeed right-side tray.
   */
  const noNav = true;

  return (
    <>
      <AuthGuard>

        <Suspense fallback={<RouteLoading />}>
          <Routes>

            <Route
              path="/"
              element={
                <PrivateRoute>
                  <Home />
                </PrivateRoute>
              }
            />

            <Route path="/login" element={<Login />} />

            <Route path="/register" element={<Register />} />

            <Route path="/discover" element={<Discover />} />

            <Route
              path="/profile/:userId"
              element={<UserProfile />}
            />

            <Route
              path="/inbox"
              element={
                <PrivateRoute>
                  <Inbox />
                </PrivateRoute>
              }
            />

            <Route
              path="/upload"
              element={
                <PrivateRoute>
                  <Upload />
                </PrivateRoute>
              }
            />

            <Route
              path="/wallet"
              element={
                <PrivateRoute>
                  <Wallet />
                </PrivateRoute>
              }
            />

            <Route
              path="/profile"
              element={
                <PrivateRoute>
                  <Profile />
                </PrivateRoute>
              }
            />

            <Route
              path="/admin-login"
              element={<AdminLogin />}
            />

            <Route
              path="/contract"
              element={
                <PrivateRoute>
                  <Contract />
                </PrivateRoute>
              }
            />

            <Route
              path="/reset-password"
              element={<PasswordReset />}
            />
            <Route
              path="/verify-email"
              element={<VerifyEmail />}
            />

            <Route
              path="/admin-reset"
              element={<PasswordReset />}
            />

            <Route
              path="/admin"
              element={
                <AdminRoute>
                  <AdminDashboard />
                </AdminRoute>
              }
            />

            <Route path="/about" element={<About />} />

            <Route path="/privacy" element={<Privacy />} />

            <Route path="/terms" element={<Terms />} />

            <Route path="/cookies" element={<Cookies />} />

            <Route
              path="/guidelines"
              element={<CommunityGuidelines />}
            />

            <Route
              path="/advertiser-policy"
              element={<AdvertiserPolicy />}
            />

            <Route
              path="/creator-policy"
              element={<CreatorPolicy />}
            />

            <Route path="/dmca" element={<DMCA />} />

            <Route path="/contact" element={<Contact />} />

          </Routes>
        </Suspense>

      </AuthGuard>

      {noNav ? null : null}

      <PWAInstallPrompt />
      <InstallPrompt />
    </>
  );
}


/*
 * Authentication guard.
 *
 * This remains outside the lazy routes so authentication
 * state is resolved before protected pages render.
 */
function AuthGuard({ children }) {
  const { loading } = useAuth();
  const location = useLocation();

  const publicPaths = [
    "/login",
    "/register",
    "/password-reset",
    "/admin-login",
    "/verify-email"
  ];

  const isPublic = publicPaths.some(
    (path) => location.pathname.startsWith(path)
  );

  /*
   * Keep the variable intentionally referenced so the public
   * route calculation remains explicit for future auth handling.
   */
  void isPublic;

  if (loading) {
    return <RouteLoading />;
  }

  return children;
}


function App() {
  return (
    <AuthProvider>
      <Router>
        <Layout />
      </Router>
    </AuthProvider>
  );
}

export default App;
